/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

/**
 * Platform types for homepage.
 * - 'ios' / 'android': explicit native-app platforms for product tile targeting
 * - 'mobile': legacy native-app umbrella (backward-compatible)
 * - 'web-mobile' / 'web': web platforms
 */
export type Platform = 'ios' | 'android' | 'mobile' | 'web-mobile' | 'web';

export const HOMEPAGE_BLOCK_IDS = {
  SPOTLIGHT_CARDS: 'block_spotlight_cards',
  PRODUCT_TILES: 'block_product_tiles',
  PRIME_ADS: 'block_prime_ads',
  NOTIFICATION_BANNER: 'block_notification_banner',
  RECOMMENDATION_TABS: 'block_recommendation_tabs',
  COOKIES_BANNER: 'block_cookies_banner',
  LOGIN_BANNER: 'block_login_banner',
  HERO_BANNER: 'block_hero_banner',
  ARTICLE_CAROUSEL: 'block_article_carousel',
  FOOTER_ADS_BANNER: 'block_footer_ads_banner',
  LOB_SEARCH_TABS: 'block_lob_search_tabs',
  BOTTOM_NAVIGATION: 'block_bottom_navigation',
  DYNAMIC_SPLASH: 'block_dynamic_splash',
} as const;

/**
 * Derived type for Block IDs to ensure strict type checking across the app.
 */
export type HomepageBlockId =
  (typeof HOMEPAGE_BLOCK_IDS)[keyof typeof HOMEPAGE_BLOCK_IDS];

/**
 * Common field patterns to reduce duplicate definitions
 */
interface WithRedirection {
  redirectionUrl: string;
}

interface Identifiable {
  id: string;
  uuid: string;
}

export interface BackgroundImageSet {
  src: string;
  srcSet?: string;
}

/**
 * Base block structure with Strict ID Literal Mapping
 */
export interface BaseBlock<ID extends string, T = unknown> {
  id: ID;
  uuid: string;
  order: number;
  data: T;
}

/**
 * Spotlight Cards Block (Mobile)
 */
export interface SpotlightCard extends Identifiable, WithRedirection {
  name: string;
}

export interface SpotlightCardsData {
  cards: SpotlightCard[];
}

export type SpotlightCardsBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.SPOTLIGHT_CARDS,
  SpotlightCardsData
>;

/**
 * Product Tiles Block (All platforms)
 */
export interface ProductTile extends WithRedirection {
  productId: string;
  title: string;
  attractText?: string;
  iconUrl: string;
  isSearchTab?: boolean;
}

export interface ProductTilesData {
  tiles: ProductTile[];
  showAll?: {
    label: string;
    iconUrl: string;
    dialogText: string;
  };
}

export type ProductTilesBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.PRODUCT_TILES,
  ProductTilesData
>;

/**
 * Prime Ads Block (All platforms)
 */
export interface PrimeAdsData {
  primeAd: {
    adUnitId: string;
    templateId: string;
    adId: string;
  };
}

export type PrimeAdsBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.PRIME_ADS,
  PrimeAdsData
>;

/**
 * Notification Banner Block (All platforms)
 */
export interface CallToAction {
  text: string;
  url: string;
}

export interface NotificationBannerData {
  notificationBanner: {
    iconUrl: string;
    text: string;
    callToAction?: CallToAction;
  };
}

export type NotificationBannerBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.NOTIFICATION_BANNER,
  NotificationBannerData
>;

/**
 * Recommendation Tabs Block (All platforms)
 */
export interface RecommendationTab {
  id: string;
  title: string;
}

export interface RecommendationTabsData {
  tabs: RecommendationTab[];
}

export type RecommendationTabsBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.RECOMMENDATION_TABS,
  RecommendationTabsData
>;

/**
 * Cookies Banner Block (Web, Web-Mobile)
 */
export interface CookiesBannerData {
  cookiesBanner: {
    text: string;
    privacyStatementLink: string;
    closeIconUrl: string;
  };
}

export type CookiesBannerBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.COOKIES_BANNER,
  CookiesBannerData
>;

/**
 * Login Banner Block (Web-Mobile)
 */
export interface LoginBannerData {
  loginBanner: {
    text: string;
    closeIconUrl: string;
    loginButton: {
      text: string;
      redirectionUrl: string;
    };
  };
}

export type LoginBannerBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.LOGIN_BANNER,
  LoginBannerData
>;

/**
 * Hero Banner Block (Web, Web-Mobile)
 */
export interface HeroBanner extends WithRedirection {
  label?: string;
  headline?: string;
  subHeadline?: string;
  promoCode: string;
  tnc: string;
  backgroundImage?: BackgroundImageSet;
}

export interface HeroBannerData {
  banners: HeroBanner[];
}

export type HeroBannerBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.HERO_BANNER,
  HeroBannerData
>;

/**
 * Article Carousel Block (Web, Web-Mobile)
 */
export interface Article extends WithRedirection {
  title: string;
  subTitle: string;
  backgroundImage?: BackgroundImageSet;
  backgroundImageUrl?: string;
}

export interface ArticleSection {
  title: string;
  articles: Article[];
}

export interface ArticleCarouselData {
  sections: ArticleSection[];
}

export type ArticleCarouselBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.ARTICLE_CAROUSEL,
  ArticleCarouselData
>;

/**
 * Footer Ads Banner Block (Web, Web-Mobile)
 */
export interface FooterAdsBanner {
  widgetName: string;
  adUnitId: string;
  templateId: string;
  adId: string;
}

export interface FooterAdsBannerData {
  banners: FooterAdsBanner[];
}

export type FooterAdsBannerBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.FOOTER_ADS_BANNER,
  FooterAdsBannerData
>;

/**
 * LOB Search Tabs Block (Web)
 */
export interface LobSearchTab {
  id: string;
  title: string;
  iconUrl: string;
}

export interface LobSearchTabsData {
  tabs: LobSearchTab[];
}

export type LobSearchTabsBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.LOB_SEARCH_TABS,
  LobSearchTabsData
>;

/**
 * Bottom Navigation Block (Web / web-mobile)
 * Items and labels are fully CMS-driven; icon is an asset URL.
 */
export interface BottomNavigationItem extends WithRedirection {
  navigationId: string;
  label: string;
  iconUrl: string;
}

export interface BottomNavigationData {
  items: BottomNavigationItem[];
}

export type BottomNavigationBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.BOTTOM_NAVIGATION,
  BottomNavigationData
>;

export type DynamicSplashWidgetType = 'image' | 'video' | 'lottie';

export interface DynamicSplashMediaUrls {
  image?: string | null;
  video?: string | null;
  lottie?: string | null;
}

export interface DynamicSplashCampaign {
  campaign_id: string;
  campaign_version: number;
  priority: number;
  widget_type: DynamicSplashWidgetType;
  starts_at: string;
  expires_at: string;
  backgroundColor: string;
  url: DynamicSplashMediaUrls;
}

/** Native-app payload forwarded to the host without being rendered by Home. */
export interface DynamicSplashData {
  campaigns: DynamicSplashCampaign[];
}

export type DynamicSplashBlock = BaseBlock<
  typeof HOMEPAGE_BLOCK_IDS.DYNAMIC_SPLASH,
  DynamicSplashData
>;

/**
 * Union type for all block types
 */
export type HomepageBlock =
  | SpotlightCardsBlock
  | ProductTilesBlock
  | PrimeAdsBlock
  | NotificationBannerBlock
  | RecommendationTabsBlock
  | CookiesBannerBlock
  | LoginBannerBlock
  | HeroBannerBlock
  | ArticleCarouselBlock
  | FooterAdsBannerBlock
  | LobSearchTabsBlock
  | BottomNavigationBlock
  | DynamicSplashBlock;

/**
 * Homepage metadata
 */
export interface HomepageMetadata {
  timestamp: string;
  version: string;
  consumer: {
    lob: string;
    feature: string;
  };
}

/**
 * Complete homepage response structure
 */
export interface HomepageBlockResponse {
  success: boolean;
  data: {
    blocks: HomepageBlock[];
    currency: string;
  };
  metadata: HomepageMetadata;
  _isFallback?: boolean;
}
